import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import type { LoginProps } from './Login.types';
import { useLogin } from './Login.hook';
import * as styles from './Login.styles';
import { getLangTitle } from './Login.utils';

export const Login: React.FC<LoginProps> = () => {
  const {
    t,
    i18n,
    email,
    setEmail,
    password,
    setPassword,
    errorMessage,
    loading,
    token,
    handleSubmit,
    changeLanguage,
  } = useLogin();

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.bgGlowPrimary}></div>
      <div className={styles.bgGlowAccent}></div>

      <div className={styles.glassCard}>
        
        <div className={styles.headerContainer}>
          <div className={styles.headerLeft}>
            <img 
              src="/logo.svg" 
              alt="WealthIQ Icon" 
              className={styles.logoImg} 
            />
            <span className={styles.titleText}>
              Wealth<span className={styles.titleHighlight}>
                IQ
                <span className={styles.titleBadge}>
                  Pro
                </span>
              </span>
            </span>
          </div>

          <div className={styles.langControlsContainer}>
            {(['en', 'hi', 'te', 'pa'] as const).map((lang) => {
              const isActive = (i18n.language || 'en').startsWith(lang);
              return (
                <button
                  key={lang}
                  onClick={() => changeLanguage(lang)}
                  className={styles.getLangBtnClass(isActive)}
                  title={getLangTitle(lang)}
                >
                  {lang}
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.headingContainer}>
          <h2 className={styles.headingTitle}>
            {t('loginWelcome', 'Welcome Back')}
          </h2>
          <p className={styles.headingSubtitle}>
            {t('loginSubtitle', 'Sign in to access your wealth intelligence dashboard')}
          </p>
        </div>

        {errorMessage && (
          <div className={styles.errorContainer}>
            <i className="fa-solid fa-triangle-exclamation"></i>
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.formContainer}>
          <div className={styles.inputGroup}>
            <label htmlFor="login-email" className={styles.label}>
              {t('email', 'Email Address')}
            </label>
            <div className={styles.inputWrapper}>
              <i className={`${styles.iconClass} fa-envelope`}></i>
              <input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                placeholder="developer@wealthiq.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={styles.inputField}
                required
              />
            </div>
          </div>

          <div className={styles.inputGroup}>
            <div className={styles.passwordHeader}>
              <label htmlFor="login-password" className={styles.label}>
                {t('password', 'Password')}
              </label>
              <Link 
                to="/forgot-password"
                className={styles.forgotPasswordLink}
              >
                {t('forgotPassword', 'Forgot Password?')}
              </Link>
            </div>
            <div className={styles.inputWrapper}>
              <i className={`${styles.iconClass} fa-lock`}></i>
              <input
                id="login-password"
                data-testid="login-password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={styles.inputField}
                required
              />
            </div>
          </div>

          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className={styles.submitButton}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-circle-notch animate-spin"></i>
                <span>{t('signingIn', 'Signing In...')}</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-right-to-bracket"></i>
                <span>{t('signIn', 'Sign In')}</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
